import { EntityManagement } from '@genesislcap/foundation-entity-management';
import { Form, Filters } from '@genesislcap/foundation-forms';
import { foundationLayoutComponents } from '@genesislcap/foundation-layout';
import { getApp } from '@genesislcap/foundation-shell/app';
import {FoundationRouter, Modal} from '@genesislcap/foundation-ui';
import * as zeroDesignSystem from '@genesislcap/foundation-zero';
import { g2plotChartsComponents } from '@genesislcap/g2plot-chart';
import * as rapidDesignSystem from '@genesislcap/rapid-design-system';
import { rapidGridComponents } from '@genesislcap/rapid-grid-pro';
import { TrancheDetailFlyout } from '../routes/mbm/bond-bidding/tranche-detail-flyout';
import { UploadValidationFlyout } from '../routes/mbm/bond-bidding/upload-validation-flyout';
import { NotPermittedComponent } from '../../mbm/components/not-permitted-component';
import { MbmGridPro } from '../../mbm/components/mbm-grid-pro';
import '../../mbm/components/account-details';
import {SyndicateInfo} from "../../pbi/components/deals/syndicate-info";
import {DealSeries} from "../../pbi/components/deals/deal-series/deal-series";
import {AllocationDonut} from "../../pbi/components/deals/charts/allocation-donut";
import {SparkLine} from "../../pbi/components/deals/charts/spark-line";
import {MuniDeals} from "../../pbi/components/deals/deals";
import {css, ElementDefinitionContext, ElementStyles, FoundationElementDefinition} from "@genesislcap/web-core";
import {HelpMenu} from "../../pbi/layouts/nav/menu-help/help-menu";
import {HelpDialog} from "../../pbi/components/help-dialog/help-dialog";
import {Profiles, Users} from "@genesislcap/pbc-auth-ui";
import {cancelButtonBg} from "../../pbi/styles/styles";

const customRapidTabStyles = (
  context: ElementDefinitionContext,
  definition: FoundationElementDefinition
): ElementStyles => css`
  ${rapidDesignSystem.rapidTabStyles(context, definition)}

  :host {
    color: var(--neutral-foreground-rest);
    padding: 20px;
  }

  ::slotted([role='tab'][aria-selected='true']) {
    color: var(--neutral-foreground-rest);
  }

  :host:not([appearance="secondary"]) {
    border-width: 1px;
    background: #1f282d;
    border-color: transparent;
  }

  [aria-selected="true"] {
    color: white;
    background: var(--black-color);
    border-width: 1px;
    font-weight: 700;
    border-color: ${cancelButtonBg};
  }
`;

const customRapidTabsStyles = (
  context: ElementDefinitionContext,
  definition: FoundationElementDefinition
): ElementStyles => css`
  ${rapidDesignSystem.rapidTabsStyles(context, definition)}

  :host {
    background: #1f282d;
  }

  :host([appearance="secondary"]),
  :host([appearance='secondary']:hover) {
    background: #181A1f;
  }

  ::slotted([role='tab']) {
    color: color-mix(in srgb, var(--neutral-foreground-rest), transparent 50%);
    background: transparent;
    border-left: 1px solid transparent;
    border-left-color: ${cancelButtonBg};
    box-shadow: none;
    border-radius: 4px 4px 0 0;
    font-weight: 500;
  }

  ::slotted([role='tab']:first-child) {
    border-left-color: transparent;
  }

  ::slotted([role='tab'][aria-selected='true']) {
    color: var(--neutral-foreground-rest);
    background: #1d2024;
    border: 1px solid ${cancelButtonBg};
    border-bottom-color: transparent;
    font-weight: 700;
  }

  :host .tablist {
    background: #1f282d;
    margin-left: 8px;
    padding-top: 5px;
    padding-bottom: 0;
    margin-bottom: -2px;
  }

  :host rapid-tab-panel {
    background-color: #181a1f;
  }

  :host(.nested-layout[appearance="secondary"]) .tablist {
    margin-bottom: 0;
    border-top: 0;
  }

  :host([appearance="secondary"]) .tablist {
    background: rgb(29, 32, 36);
    margin-left: 0;
    padding: 0;
    width: 100%;
    display: block;
    margin-bottom: 0;
    border-bottom: 1px solid ${cancelButtonBg};
    border-top: 1px solid ${cancelButtonBg};
  }

  :host([appearance="secondary"]) ::slotted([role="tab"]) {
    padding: 0 16px;
    background: rgb(29, 32, 36);
    width: fit-content;
    border: 0;
    height: 32px;
  }

  :host([appearance="secondary"]) ::slotted([role="tab"][aria-selected="true"]) {
    border-bottom: 1px solid var(--accent-fill-rest);
  }

  :host([appearance="secondary"]) .tablist .activeIndicator {
    display: none;
  }

  :host(:not([appearance="secondary"])) ::slotted([role='tab']) {
    padding: 20px;
  }

  .tablist,
  :host::part(start),
  :host::part(end) {
    background: #1f282d;
  }

  :host([orientation='horizontal']) ::slotted([role='tabpanel'][slot='tabpanel']) {
    padding: 0;
  }

  :host([orientation='horizontal']) ::slotted(.padded[role='tabpanel'][slot='tabpanel']) {
    padding: 8px;
  }
`;

const customRapidTabPanelStyles = (
  context: ElementDefinitionContext,
  definition: FoundationElementDefinition
): ElementStyles => css`
  ${rapidDesignSystem.rapidTabPanelStyles(context, definition)}

  :host {
    background: #181a1f;
  }
`;

/**
 * Ensure tree shaking doesn't remove these.
 */
FoundationRouter;
EntityManagement;
Form;
NotPermittedComponent;
TrancheDetailFlyout;
UploadValidationFlyout;
MbmGridPro;
Filters;

MuniDeals;
SparkLine;
AllocationDonut;
DealSeries;
SyndicateInfo;
HelpDialog;
HelpMenu;
Modal;

Users;
Profiles;

/**
 * registerComponents.
 * @public
 */
export async function registerComponents() {
  const { configure: configureHeader } = await import('@genesislcap/foundation-header/config');
  /**
   * Register any PBC components with the design system
   */
  getApp().registerComponents({
    designSystem: rapidDesignSystem,
  });

  rapidDesignSystem
    .provideDesignSystem()
    .register(
      rapidDesignSystem.rapidTab({
        styles: customRapidTabStyles
      }),
      rapidDesignSystem.rapidTabs({
        styles: customRapidTabsStyles
      }),
      rapidDesignSystem.rapidTabPanel({
        styles: customRapidTabPanelStyles
      }),
      rapidDesignSystem.baseComponents,
      rapidGridComponents,
      g2plotChartsComponents,
      foundationLayoutComponents,
    );

  configureHeader({
    templateOptions: {
      icon: 'rapid-icon',
      button: 'rapid-button',
      connectionIndicator: 'rapid-connection-indicator',
      select: 'rapid-select',
      option: 'rapid-option',
      flyout: 'rapid-flyout',
    },
  });

  /**
   * Still required while we transition all PBCs to rapid. Remove when complete.
   */
  zeroDesignSystem
    .provideDesignSystem()
    .register(zeroDesignSystem.baseComponents, g2plotChartsComponents, foundationLayoutComponents);
}

